import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { StarRating } from './StarRating';
import { Button } from '../button';

const ModalOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-modal w-full max-w-md mx-4 overflow-hidden`}
`;

const ModalHeader = styled.div`
  ${tw`flex justify-between items-center px-6 py-4 border-b border-border-default`}
`;

const Title = styled.h2`
  ${tw`text-lg font-semibold`}
`;

const CloseButton = styled.button`
  ${tw`text-text-tertiary hover:text-text-secondary cursor-pointer`}
`;

const ModalBody = styled.div`
  ${tw`px-6 py-6`}
`;

const GuestInfo = styled.div`
  ${tw`flex items-center gap-3 mb-6`}
`;

const Avatar = styled.div`
  ${tw`w-12 h-12 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary font-semibold text-lg`}
`;

const GuestDetails = styled.div`
  ${tw`flex flex-col`}
`;

const GuestName = styled.span`
  ${tw`font-semibold text-text-primary`}
`;

const TripDetails = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const RatingSection = styled.div`
  ${tw`flex flex-col items-center py-4`}
`;

const CommentSection = styled.div`
  ${tw`mt-6`}
`;

const CommentLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const CommentTextarea = styled.textarea`
  ${tw`w-full px-4 py-3 border border-border-default rounded-lg resize-none focus:ring-2 focus:ring-black focus:border-transparent`}
  height: 120px;
`;

const ButtonContainer = styled.div`
  ${tw`px-6 py-4 border-t border-border-default`}
`;

const SubmitButton = styled.button`
  ${tw`w-full py-3 px-4 bg-black text-white font-semibold rounded-lg hover:bg-black-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
`;

interface HostRatingGuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: { general: number; comment?: string }) => void;
  guestName: string;
  guestAvatar?: string;
  carName: string;
  tripDates: string;
  isSubmitting?: boolean;
}

export const HostRatingGuestModal = ({
  isOpen,
  onClose,
  onSubmit,
  guestName,
  guestAvatar,
  carName,
  tripDates,
  isSubmitting = false,
}: HostRatingGuestModalProps) => {
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (rating === 0) return;
    onSubmit({
      general: rating,
      comment: comment.trim() || undefined,
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>Rate this guest</Title>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <GuestInfo>
            <Avatar>
              {guestAvatar ? (
                <img
                  src={guestAvatar}
                  alt={guestName}
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                getInitials(guestName)
              )}
            </Avatar>
            <GuestDetails>
              <GuestName>{guestName}</GuestName>
              <TripDetails>
                {carName} - {tripDates}
              </TripDetails>
            </GuestDetails>
          </GuestInfo>

          <RatingSection>
            <StarRating rating={rating} onRatingChange={setRating} showLabel />
          </RatingSection>

          <CommentSection>
            <CommentLabel>Write a review (optional)</CommentLabel>
            <CommentTextarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your experience with this guest..."
            />
          </CommentSection>
        </ModalBody>

        <ButtonContainer>
          <SubmitButton onClick={handleSubmit} disabled={rating === 0 || isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </SubmitButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};
